/**
 * 策略表现图表组件
 */

import React, { useEffect, useRef } from 'react';
import { Card, Spin, Alert, Row, Col, Statistic, Tabs } from 'antd';
import * as echarts from 'echarts';
import type { EChartsOption } from 'echarts';
import { PerformanceChartProps } from './types';

const { TabPane } = Tabs;

const PerformanceChart: React.FC<PerformanceChartProps> = ({
  data,
  showBenchmark = true,
  showDrawdown = true,
  showMetrics = true,
  loading = false,
  error,
  config,
  onDataPointClick,
  onZoomChange,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!chartRef.current || loading || error || !data.length) return;

    // 初始化图表
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    // 准备数据
    const cumulativeReturnData = data.map(item => [
      item.timestamp,
      item.cumulative_return
    ]);

    const benchmarkData = showBenchmark && data[0]?.benchmark_return !== undefined
      ? data.map(item => [item.timestamp, item.benchmark_return])
      : [];

    const drawdownData = showDrawdown && data[0]?.drawdown !== undefined
      ? data.map(item => [item.timestamp, item.drawdown])
      : [];

    // 配置图表选项
    const option: EChartsOption = {
      backgroundColor: config?.theme?.backgroundColor || 'transparent',
      animation: config?.animation !== false,
      grid: [
        {
          left: '3%',
          right: '4%',
          top: '10%',
          height: showDrawdown ? '60%' : '80%',
          show: config?.showGrid !== false,
        },
        ...(showDrawdown ? [{
          left: '3%',
          right: '4%',
          top: '75%',
          height: '20%',
          show: config?.showGrid !== false,
        }] : [])
      ],
      xAxis: [
        {
          type: 'time',
          gridIndex: 0,
        },
        ...(showDrawdown ? [{
          type: 'time' as const,
          gridIndex: 1,
        }] : [])
      ],
      yAxis: [
        {
          type: 'value',
          gridIndex: 0,
          name: '累计收益率 (%)',
          axisLabel: {
            formatter: (value: number) => `${(value * 100).toFixed(2)}%`
          }
        },
        ...(showDrawdown ? [{
          type: 'value' as const,
          gridIndex: 1,
          name: '回撤 (%)',
          axisLabel: {
            formatter: (value: number) => `${(value * 100).toFixed(2)}%`
          }
        }] : [])
      ],
      series: [
        {
          name: '累计收益率',
          type: 'line',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: cumulativeReturnData,
          smooth: true,
          lineStyle: {
            color: '#1890ff',
            width: 2
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(24, 144, 255, 0.3)' },
                { offset: 1, color: 'rgba(24, 144, 255, 0.1)' }
              ]
            }
          }
        },
        ...(benchmarkData.length > 0 ? [{
          name: '基准收益率',
          type: 'line' as const,
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: benchmarkData,
          smooth: true,
          lineStyle: {
            color: '#52c41a',
            width: 2,
            type: 'dashed' as const
          }
        }] : []),
        ...(drawdownData.length > 0 ? [{
          name: '回撤',
          type: 'line' as const,
          xAxisIndex: 1,
          yAxisIndex: 1,
          data: drawdownData,
          smooth: true,
          lineStyle: {
            color: '#ff4d4f',
            width: 2
          },
          areaStyle: {
            color: 'rgba(255, 77, 79, 0.2)'
          }
        }] : [])
      ],
      tooltip: {
        trigger: 'axis',
        show: config?.showTooltip !== false,
        formatter: (params: any) => {
          let result = `${params[0].axisValueLabel}<br/>`;
          params.forEach((param: any) => {
            const value = param.data[1];
            const formattedValue = typeof value === 'number' 
              ? `${(value * 100).toFixed(2)}%` 
              : 'N/A';
            result += `${param.seriesName}: ${formattedValue}<br/>`;
          });
          return result;
        },
      },
      legend: {
        show: config?.showLegend !== false,
        top: '5%'
      },
      dataZoom: config?.showZoom !== false ? [
        {
          type: 'inside',
          start: 0,
          end: 100,
          xAxisIndex: [0, 1]
        },
        {
          start: 0,
          end: 100,
          xAxisIndex: [0, 1]
        }
      ] : undefined,
    };

    chartInstance.current.setOption(option);

    // 绑定事件
    if (onDataPointClick) {
      chartInstance.current.on('click', (params: any) => {
        const dataIndex = params.dataIndex;
        if (dataIndex >= 0 && data[dataIndex]) {
          onDataPointClick(data[dataIndex]);
        }
      });
    }

    if (onZoomChange) {
      chartInstance.current.on('datazoom', (params: any) => {
        const startIndex = Math.floor((params.start / 100) * data.length);
        const endIndex = Math.floor((params.end / 100) * data.length);
        if (data[startIndex] && data[endIndex]) {
          onZoomChange(data[startIndex].timestamp, data[endIndex].timestamp);
        }
      });
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [data, showBenchmark, showDrawdown, config, loading, error, onDataPointClick, onZoomChange]);

  // 计算统计指标
  const calculateMetrics = () => {
    if (!data.length) return {};

    const returns = data.map(item => item.cumulative_return);
    const finalReturn = returns[returns.length - 1] || 0;
    const maxDrawdown = Math.min(...data.map(item => item.drawdown || 0));
    const avgSharpe = data.reduce((sum, item) => sum + (item.sharpe_ratio || 0), 0) / data.length;

    return {
      totalReturn: finalReturn,
      maxDrawdown,
      sharpeRatio: avgSharpe,
      volatility: calculateVolatility(returns)
    };
  };

  const calculateVolatility = (returns: number[]) => {
    if (returns.length < 2) return 0;
    
    const mean = returns.reduce((sum, ret) => sum + ret, 0) / returns.length;
    const variance = returns.reduce((sum, ret) => sum + Math.pow(ret - mean, 2), 0) / (returns.length - 1);
    return Math.sqrt(variance);
  };

  const metrics = calculateMetrics();

  if (error) {
    return <Alert message="图表加载失败" description={error} type="error" showIcon />;
  }

  return (
    <Card title="策略表现分析" loading={loading}>
      {showMetrics && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Statistic
              title="总收益率"
              value={metrics.totalReturn || 0}
              precision={2}
              suffix="%"
              valueStyle={{ color: (metrics.totalReturn || 0) >= 0 ? '#3f8600' : '#cf1322' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="最大回撤"
              value={Math.abs(metrics.maxDrawdown || 0)}
              precision={2}
              suffix="%"
              valueStyle={{ color: '#cf1322' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="夏普比率"
              value={metrics.sharpeRatio || 0}
              precision={3}
              valueStyle={{ color: (metrics.sharpeRatio || 0) >= 1 ? '#3f8600' : '#faad14' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="波动率"
              value={metrics.volatility || 0}
              precision={2}
              suffix="%"
            />
          </Col>
        </Row>
      )}
      <div ref={chartRef} style={{ width: '100%', height: '400px' }} />
    </Card>
  );
};

export default PerformanceChart;